import React, { useState, useEffect } from "react";
import AlpineAirTherapyModal from "./modals/AlpineAirTherapyModal";
import { useLanguage } from "@/contexts/LanguageContext";
import { useServiceList } from "@/contexts/ServiceListContext";
import { Button } from "@/components/ui/button";
import { OptimizedImage } from "@/components/OptimizedImage";
import { getSpaBlurPlaceholder } from "@/utils/spaBlurPlaceholders";

const SpaHeroSection: React.FC = () => {
  const [scrollY, setScrollY] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { t } = useLanguage();
  const { setIsTrayOpen } = useServiceList();

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleOpenServiceList = () => {
    setIsTrayOpen(true, 'spa_hero');
  };

  return (
    <section className="relative w-full h-screen" data-oid="1wiba3k">
      {/* Full Screen Hero Image */}
      <div
        className="relative h-full w-full overflow-hidden"
        data-oid="c.n81pa"
      >
        <OptimizedImage
          src="/images/Clinique Nutraswiss/Spa Hero.desktop.webp"
          alt="Alpine wellness spa experience"
          className="h-full w-full object-cover"
          priority={true}
          fetchPriority="high"
          webpSrc="/images/Clinique Nutraswiss/Spa Hero.mobile.webp 768w, /images/Clinique Nutraswiss/Spa Hero.tablet.webp 1024w, /images/Clinique Nutraswiss/Spa Hero.desktop.webp 1920w"
          srcSet="/images/Clinique Nutraswiss/Spa Hero.mobile.webp 768w, /images/Clinique Nutraswiss/Spa Hero.tablet.webp 1024w, /images/Clinique Nutraswiss/Spa Hero.desktop.webp 1920w"
          sizes="100vw"
          blurDataURL={getSpaBlurPlaceholder("/images/Clinique Nutraswiss/Spa Hero.desktop.webp")}
          data-oid="gx2j4:p"
        />

        {/* Gradient overlay */}
        <div
          className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/20"
          data-oid="dktkw8m"
        ></div>

        {/* Overlay text elements - Centered */}
        <div
          className="absolute inset-0 z-20 flex flex-col items-center justify-center text-center px-4 md:px-8 lg:px-16"
          data-oid=":mt:_kr"
        >
          {/* Main Title */}
          <h1
            className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-playfair text-elegant-white mb-6 md:mb-8 leading-tight drop-shadow-lg"
            data-oid=":kfa:vc"
          >
            {t("spa.hero.mainTitle")}
          </h1>

          {/* Sub-message */}
          <p
            className="text-lg md:text-xl lg:text-2xl xl:text-3xl font-playfair italic text-elegant-white/95 mb-8 md:mb-12 max-w-4xl drop-shadow-md leading-relaxed"
            data-oid="gc:0fgh"
          >
            {t("spa.hero.subMessage")}
          </p>

          {/* Parallax Container for CTA Button */}
          <div
            className="transition-transform duration-300 ease-out"
            style={{
              transform: `translateY(${-scrollY * 0.1}px)`,
            }}
            data-oid="lbz6b3g"
          >
            {/* CTA Button */}
            <Button
              onClick={handleOpenServiceList}
              trackingType="navigation"
              trackingLabel={t("spa.hero.ctaButton")}
              trackingLocation="spa_hero"
              trackingData={{ action: "open_service_list" }}
              className="bg-alpine-gold hover:bg-alpine-gold/90 text-white px-8 py-4 md:px-10 md:py-5 text-base md:text-lg font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              data-oid="4m97ypo"
            >
              {t("spa.hero.ctaButton")}
            </Button>
          </div>
        </div>
      </div>

      {/* Modal */}
      <AlpineAirTherapyModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        data-oid="k-ok_.z"
      />
    </section>
  );
};

export default SpaHeroSection;
